/* The capability matrix over the real API. No `X-Lumiq-Tenant` header — hard rule 1.
 *
 * Two reads and one run. The run is a **POST with an `Idempotency-Key`**, like anomaly's evaluation and
 * unlike rulegen's PUT: `ai-evals.yaml` states the rule — each run *appends* evidence and a retry
 * appends again, so the key is what makes a double-click one run. Re-running after a prompt change or a
 * model swap is the whole point, and the matrix reads the latest while every earlier run stays.
 *
 * The key is forgotten after success so a deliberate second run is a second decision, not a replay —
 * and here that matters more than on any other surface, because a run is real model usage. On a hosted
 * endpoint it is an invoice, and `Meter.LLM_TOKENS` counts it whether the output was accepted or
 * refused.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isBar,
  isCapabilityMatrix,
  isCapabilityResult,
  isSuiteCapability,
  isSuiteReport,
} from '@/shared/api/generated/ai-evals';
import type {
  Capability,
  CapabilityMatrix,
  CapabilityResult,
  SuiteReport,
} from '@/shared/api/generated/ai-evals';
import { forgetIdempotencyKey, getOne, idempotencyKeyFor, post } from '@/shared/api/http';

export const MATRIX_URL = '/v1/ai/evals/matrix';
export const SUITE_URL = '/v1/ai/evals/suite';

export type {
  Bar,
  Capability,
  CapabilityMatrix,
  CapabilityResult,
  CapabilityVerdict,
  CaseRecord,
  CaseVerdict,
  SuiteCapability,
  SuiteReport,
} from '@/shared/api/generated/ai-evals';

/** The generated envelope guard plus the per-item checks it does not make.
 *
 * `isCapabilityMatrix` validates `Array.isArray(results)` and `bars !== undefined` — so a renamed
 * `verdict`, a missing one, or a `null` `bars` all pass. That is generator-wide and pre-existing; what
 * is not acceptable is a *consumer* relying on it: an unrecognised verdict rendered as the permitted
 * tone until `verdictTone` became a total map, and `bars[capability]` on a null throws at render, where
 * the ErrorBoundary turns a bad field into a blank screen.
 *
 * `getPage`'s docstring states the rule this restores — *"One bad item fails the whole page. Showing
 * eleven of twelve rows without saying so is worse than showing none"* — and `getOne` plus an envelope
 * guard validated none. The same shape `core.ai.anomaly`'s slice uses for its findings list.
 */
function isMatrix(value: unknown): value is CapabilityMatrix {
  if (!isCapabilityMatrix(value)) return false;
  const candidate = value as unknown as Record<string, unknown>;
  const bars = candidate['bars'];
  if (typeof bars !== 'object' || bars === null) return false;
  return (
    (candidate['results'] as unknown[]).every(isCapabilityResult) &&
    Object.values(bars).every(isBar)
  );
}

function isSuite(value: unknown): value is SuiteReport {
  if (!isSuiteReport(value)) return false;
  const capabilities = (value as unknown as Record<string, unknown>)['capabilities'];
  return Array.isArray(capabilities) && capabilities.every(isSuiteCapability);
}

export function useCapabilityMatrix(): UseQueryResult<CapabilityMatrix> {
  return useQuery({
    queryKey: ['evals', 'matrix'],
    queryFn: () => getOne(MATRIX_URL, isMatrix, 'capability matrix'),
    retry: false,
  });
}

export function useSuiteReport(): UseQueryResult<SuiteReport> {
  return useQuery({
    queryKey: ['evals', 'suite'],
    queryFn: () => getOne(SUITE_URL, isSuite, 'evaluation suite'),
    /* Five minutes. The corpus is code — it changes when the deployment changes, not while somebody is
       reading — and `meets_specification` is the sentence that says how much a PROVEN verdict is worth,
       so it should not blink between refetches. */
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Runs one capability's suite against this tenant's routed model.
 *
 * Returns the `CapabilityResult` half of the outcome. `RunOutcome` is an `allOf` the emitter does not
 * handle, and the per-case records are deliberately not surfaced by this hook: a case's `reason` is
 * host-written and safe, but the screen's job is the verdict and the counts it was derived from, and a
 * per-case list is a debugging view for whoever is fixing a prompt.
 */
export function useRunEvaluation(): UseMutationResult<
  CapabilityResult,
  Error,
  Capability
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (capability: Capability) =>
      post(
        `/v1/ai/evals/runs?capability=${encodeURIComponent(capability)}`,
        idempotencyKeyFor('evals-run', capability),
        isCapabilityResult,
        'capability result',
        {},
      ),
    onSuccess: (result) => {
      forgetIdempotencyKey('evals-run', result.capability);
      void client.invalidateQueries({ queryKey: ['evals', 'matrix'] });
    },
    retry: false,
  });
}
